import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, Navigate } from 'react-router-dom';
import DefaultLayout from '../../layouts/default';
import { addRota, deleteRota, rotaCards, rotaSectionLabels, selectRota } from '../../features/rota/rotaSlice';
import { selectAllUsers } from '../../features/users/usersSlice';

const sectionStyle = 'rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_20px_45px_rgba(15,23,42,0.08)] lg:p-6';
const inputStyle = 'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100';

function monthKey(value) {
  return value.slice(0, 7);
}

function currentMonthValue() {
  return new Date().toISOString().slice(0, 7);
}

function currentDateValue() {
  return new Date().toISOString().slice(0, 10);
}

function RotaModal({ open, onClose, onSubmit, users, form, setForm, title }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/60 px-4 py-6 backdrop-blur-sm">
      <div className="flex max-h-[calc(100vh-3rem)] w-full max-w-xl flex-col rounded-[1.75rem] border border-slate-200 bg-white p-6 shadow-2xl">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.08em] text-teal-700">Add rota</p>
            <h3 className="mt-2 text-2xl font-semibold text-slate-950">{title}</h3>
          </div>
          <button type="button" onClick={onClose} className="rounded-full bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700">
            Close
          </button>
        </div>

        <form className="grid flex-1 gap-4 overflow-y-auto pr-1 pb-6" onSubmit={onSubmit}>
          <select className={inputStyle} value={form.userId} onChange={(e) => setForm((prev) => ({ ...prev, userId: e.target.value }))} required>
            <option value="">Select user</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </select>
          <input className={inputStyle} type="date" value={form.date} onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))} required />
          <textarea className={inputStyle} rows="4" value={form.note} onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))} placeholder="Optional note" />

          <div className="mt-2 flex flex-wrap gap-2 pt-2">
            <button type="submit" className="rounded-full bg-slate-950 px-4 py-3 text-sm font-semibold text-white">
              Add rota
            </button>
            <button type="button" className="rounded-full bg-slate-200 px-4 py-3 text-sm font-semibold text-slate-900" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RotaDetailPage() {
  const dispatch = useDispatch();
  const { section } = useParams();
  const rotaState = useSelector(selectRota);
  const users = useSelector(selectAllUsers);
  const [month, setMonth] = useState(currentMonthValue());
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ userId: '', date: currentDateValue(), note: '' });

  const card = useMemo(() => rotaCards.find((item) => item.key === section), [section]);
  const list = card ? rotaState[section] || [] : [];
  const filtered = list.filter((item) => monthKey(item.date) === month);

  if (!card) {
    return <Navigate to="/rota" replace />;
  }

  const selectedUser = users.find((user) => user.id === form.userId);

  const submit = (event) => {
    event.preventDefault();
    dispatch(addRota(section, {
      userId: form.userId,
      userName: selectedUser?.name || '',
      date: form.date,
      month: monthKey(form.date),
      note: form.note.trim(),
    }));
    setOpen(false);
    setForm({ userId: '', date: currentDateValue(), note: '' });
    setMonth(monthKey(form.date));
  };

  return (
    <DefaultLayout>
      <section className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="mb-2 text-sm font-semibold uppercase tracking-[0.08em] text-teal-700">Rota</p>
          <h2 className="text-[clamp(1.6rem,3vw,2.4rem)] font-semibold tracking-tight text-slate-950">
            {card.title}
          </h2>
          <p className="mt-3 max-w-2xl text-slate-600">
            Assign users to dates for the selected month and keep past rota history available through the month filter.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <Link to="/rota" className="rounded-full bg-slate-100 px-4 py-3 text-sm font-semibold text-slate-700">
            Back to rota
          </Link>
          <button type="button" className="rounded-full bg-slate-950 px-4 py-3 text-sm font-semibold text-white" onClick={() => setOpen(true)}>
            Add rota
          </button>
        </div>
      </section>

      <section className="grid gap-6">
        <section className={sectionStyle}>
          <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.08em] text-teal-700">Current month</p>
              <h3 className="mt-2 text-xl font-semibold text-slate-950">At a glance</h3>
            </div>
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-slate-600">
                Month
                <input
                  type="month"
                  className="ml-3 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-slate-900 outline-none focus:border-cyan-500 focus:ring-4 focus:ring-cyan-100"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                />
              </label>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-[1.5rem] bg-slate-50 p-5">
              <p className="text-sm text-slate-500">Selected month</p>
              <p className="mt-1 text-2xl font-semibold text-slate-950">{month}</p>
            </div>
            <div className="rounded-[1.5rem] bg-slate-50 p-5">
              <p className="text-sm text-slate-500">Entries in view</p>
              <p className="mt-1 text-2xl font-semibold text-slate-950">{filtered.length}</p>
            </div>
          </div>

          <p className="mt-4 text-sm leading-6 text-slate-600">
            Use the month picker to look back at previous rota assignments or return to the current month anytime.
          </p>
        </section>

        <div className={sectionStyle}>
          <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.08em] text-teal-700">{rotaSectionLabels[section]}</p>
              <h3 className="mt-2 text-xl font-semibold text-slate-950">Monthly rota list</h3>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-[760px] w-full border-collapse text-left">
              <thead>
                <tr className="border-b border-slate-200 text-xs font-semibold uppercase tracking-[0.05em] text-slate-500">
                  <th className="px-4 py-4">User</th>
                  <th className="px-4 py-4">Date</th>
                  <th className="px-4 py-4">Note</th>
                  <th className="px-4 py-4" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-4 py-4 text-slate-900">{item.userName}</td>
                    <td className="px-4 py-4 text-slate-600">{item.date}</td>
                    <td className="px-4 py-4 text-slate-600">{item.note || 'No note added.'}</td>
                    <td className="px-4 py-4">
                      <button
                        type="button"
                        className="rounded-full bg-rose-100 px-3 py-2 text-xs font-semibold text-rose-700"
                        onClick={() => dispatch(deleteRota({ section, id: item.id }))}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-sm text-slate-500">
                      No rota entries for this month yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <RotaModal
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={submit}
        users={users}
        form={form}
        setForm={setForm}
        title={card.title}
      />
    </DefaultLayout>
  );
}

export default RotaDetailPage;
